import { positiveModulo } from './numbers';

export type Radians = number;
export type Degrees = number;

export const PI_RADIANS_45 = Math.PI / 4;
export const PI_RADIANS_90 = Math.PI / 2;
export const PI_RADIANS_135 = 3 * (Math.PI / 4);
export const PI_RADIANS_180 = Math.PI;
export const PI_RADIANS_225 = 5 * (Math.PI / 4);
export const PI_RADIANS_270 = 3 * (Math.PI / 2);
export const PI_RADIANS_315 = 7 * (Math.PI / 4);
export const PI_RADIANS_360 = Math.PI * 2;

export function convertRadiansToDegrees(radians: Radians): Degrees {
  return radians * (180 / Math.PI);
}

/**
 * Convert radians angle to integer degrees
 */
export function convertRadiansToIntegerDegrees(radians: Radians): Degrees {
  return Math.round(convertRadiansToDegrees(radians));
}

export function convertDegreesToRadians(degrees: Degrees): Radians {
  return degrees * (Math.PI / 180);
}

/**
 * Find coterminal angle in range [0..2pi)
 */
export function normalizeAngle(angle: Radians): Radians {
  return positiveModulo(angle, PI_RADIANS_360);
}

export function isFacingRight(angle: Radians): boolean {
  const norm = normalizeAngle(angle);
  // we must exclude 270 angle to behave consistently with `faceRight` function
  return norm <= PI_RADIANS_90 || norm > PI_RADIANS_270;
}

/**
 * Transforms angle to never show an element upside down
 *
 * The algorithm finds the smallest positive coterminal angle and then transforms angles that are in the interval of
 * 90 - 270 degrees to make sure that an element doesn't face upwards
 */
export function faceRight(angle: Radians): Radians {
  const normalized = normalizeAngle(angle);
  if (normalized > PI_RADIANS_90 && normalized < PI_RADIANS_180) {
    return normalized + PI_RADIANS_180;
  }
  if (normalized >= PI_RADIANS_180 && normalized < PI_RADIANS_270) {
    return normalized - PI_RADIANS_180;
  }
  if (normalized === PI_RADIANS_270) {
    return PI_RADIANS_90;
  }
  return normalized;
}

/**
 * Get distance between two angles - distance when rotating in the closer direction, result is from [0, pi]
 */
export function getAngularDistance(angle1: Radians, angle2: Radians): Radians {
  const dist = Math.abs(normalizeAngle(angle1) - normalizeAngle(angle2));
  return dist < PI_RADIANS_180 ? dist : PI_RADIANS_360 - dist;
}

/**
 * Ger oriented distance between two angles ~ distance when rotating from fromAngle to toAngle, result is from [0, 2pi)
 */
export function getOrientedAngularDistance(
  fromAngle: Radians,
  toAngle: Radians,
): Radians {
  const from = normalizeAngle(fromAngle);
  const to = normalizeAngle(toAngle);
  return to - from + (to >= from ? 0 : PI_RADIANS_360);
}
